import { Badge, deepMerge, Stack, Text } from '@mantine/core';
import { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_TableOptions,
} from 'mantine-react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  OsLotReviewScopeParams,
  osLotReviewScopeSearch,
} from '../../lib/os-lot-review-scope';
import { OsLotReviewListItem } from '../../lib/types/os-lot-reviews';

import {
  canAttestOsLotReview,
  getLotReviewMetrics,
  reviewStatusColor,
} from './os-lot-review-checks';
import { OsLotReviewIndicatorBadges } from './os-lot-review-indicator-badges';
import { OsLotReviewMetricCell } from './os-lot-review-metric';

import { filterOrganizationColumns, usePermissions } from '@auth/data-access';
import { useFiltersTableOptions } from '@data-access';
import {
  formatOrganizationNames,
  OrganizationsList,
  PfTable,
  TsrButtonLink,
  useRowSelection,
} from '@ui';

interface OsLotReviewsTableProps {
  data: OsLotReviewListItem[];
  rowCount: number;
  isLoading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  rowSelection: MRT_RowSelectionState;
  scope: OsLotReviewScopeParams;
  setRowSelection: OnChangeFn<RowSelectionState>;
}

export const OsLotReviewsTable: FC<OsLotReviewsTableProps> = ({
  data,
  rowCount,
  isLoading,
  isFetching,
  isPlaceholderData,
  rowSelection,
  scope,
  setRowSelection,
}) => {
  const { t } = useTranslation();
  const { isAdmin, isIndustrial } = usePermissions();
  const showOrganizationColumn = isAdmin || isIndustrial;
  const filtersTableOptions = useFiltersTableOptions<OsLotReviewListItem>(
    'osLotReviews',
  );

  const lotSearch = osLotReviewScopeSearch(scope);
  const rowSelectionProps = useRowSelection<OsLotReviewListItem>({
    data,
    rowSelection,
    setRowSelection,
  });

  const columns = useMemo<MRT_ColumnDef<OsLotReviewListItem>[]>(
    () =>
      filterOrganizationColumns(
        [
          {
            accessorKey: 'farm_name',
            header: t('osLotReviews.columns.farm_name'),
            enableColumnFilter: false,
            Cell: ({ row }) => {
              const farmName = row.original.farm_name;
              if (row.original.id == null) {
                return (
                  <Text span truncate="end">
                    {farmName}
                  </Text>
                );
              }
              return (
                <TsrButtonLink
                  fullWidth
                  justify="start"
                  maw={220}
                  ml="calc(var(--button-padding-x-compact-md) * -1)"
                  params={{
                    lotId: row.original.id.toString(),
                  }}
                  search={lotSearch}
                  size="compact-md"
                  td="underline"
                  to="/os/lot-reviews/$lotId"
                  tt="capitalize"
                  variant="subtle"
                >
                  <Text span truncate="end">
                    {farmName}
                  </Text>
                </TsrButtonLink>
              );
            },
          },
          {
            id: 'organizations',
            accessorFn: (row) => formatOrganizationNames(row.organizations),
            header: t('common.properties.organization'),
            enableColumnFilter: false,
            size: 180,
            Cell: ({ row }) => (
              <OrganizationsList organizations={row.original.organizations} />
            ),
          },
          {
            id: 'species__name',
            accessorFn: (row) => row.species?.name ?? '—',
            header: t('osLotReviews.columns.crop_year'),
            enableColumnFilter: false,
            size: 140,
            Cell: ({ row }) => (
              <Stack gap={2}>
                <Text fw={600} size="sm">
                  {row.original.species?.name ?? '—'}
                </Text>
                <Text c="gray.7" size="sm">
                  {String(row.original.year)}
                </Text>
              </Stack>
            ),
          },
          {
            id: 'indicators',
            header: t('osLotReviews.columns.indicators'),
            enableColumnFilter: false,
            enableSorting: false,
            size: 180,
            Cell: ({ row }) => <OsLotReviewIndicatorBadges lot={row.original} />,
          },
          {
            accessorKey: 'real_tonnage',
            header: t('osLotReviews.columns.tonnage'),
            enableColumnFilter: false,
            size: 150,
            Cell: ({ row }) => (
              <OsLotReviewMetricCell
                metric={getLotReviewMetrics(row.original).tonnage}
                referenceLabel={t('osLotReviews.metrics.oleoze')}
              />
            ),
          },
          {
            accessorKey: 'calculated_area',
            header: t('osLotReviews.columns.area'),
            enableColumnFilter: false,
            size: 160,
            Cell: ({ row }) => (
              <OsLotReviewMetricCell
                metric={getLotReviewMetrics(row.original).area}
                referenceLabel={t('osLotReviews.metrics.theoretical')}
              />
            ),
          },
          {
            accessorKey: 'real_yield',
            header: t('osLotReviews.columns.real_yield'),
            enableColumnFilter: false,
            size: 150,
            Cell: ({ row }) => {
              const yieldMetric = getLotReviewMetrics(row.original).yield;
              return (
                <OsLotReviewMetricCell
                  metric={
                    yieldMetric.fail
                      ? yieldMetric
                      : { ...yieldMetric, reference: null }
                  }
                  referenceLabel={t('osLotReviews.metrics.range')}
                />
              );
            },
          },
          {
            accessorKey: 'review_status',
            header: t('osLotReviews.columns.review_status'),
            enableColumnFilter: false,
            size: 130,
            Cell: ({ row }) => (
              <Badge
                color={reviewStatusColor(row.original.review_status)}
                size="sm"
                variant="light"
              >
                {t(`osLotReviews.review_status.${row.original.review_status}`)}
              </Badge>
            ),
          },
        ],
        showOrganizationColumn,
      ),
    [lotSearch, showOrganizationColumn, t],
  );

  const { enableRowSelection: _baseEnableRowSelection, ...restRowSelectionProps } =
    rowSelectionProps;

  const tableOptions: MRT_TableOptions<OsLotReviewListItem> = deepMerge(
    {
      columns,
      data,
      rowCount,
      enableRowActions: false,
      enableSorting: true,
      enableColumnFilters: false,
      state: {
        isLoading,
        showLoadingOverlay: isFetching && isPlaceholderData,
        rowSelection,
      },
      mantinePaperProps: {
        h: '100%',
      },
      enableRowSelection: (row) =>
        row.id !== 'multi-edit' && canAttestOsLotReview(row.original),
      ...restRowSelectionProps,
    },
    filtersTableOptions,
  );

  return <PfTable<OsLotReviewListItem> options={tableOptions} />;
};

export default OsLotReviewsTable;
